import {useCallback, useEffect, useRef, useState} from "react";

import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {cn} from "@/lib/utils";

type TruncatedWithTooltipProps = {
  children: React.ReactNode;
  content: React.ReactNode;
  className?: string;
  contentClassName?: string;
  /** When set, clamps to N lines with break-words instead of single-line truncate. */
  maxLines?: 2 | 3;
  /** Inline-block layout for baseline alignment in compact headers (e.g. detail breadcrumb). */
  inline?: boolean;
};

/**
 * Renders children with truncate. Shows tooltip only when content is actually truncated (overflow).
 * When content fits and is fully visible, no tooltip is shown.
 */
export function TruncatedWithTooltip({
  children,
  content,
  className,
  contentClassName,
  maxLines,
  inline = false,
}: TruncatedWithTooltipProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
  }, []);

  useEffect(() => {
    checkTruncation();
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children, checkTruncation]);

  const inner = (
    <span
      className={cn(
        "min-w-0",
        inline ? "inline-block max-w-full align-baseline" : "block overflow-hidden",
      )}
    >
      <span
        ref={ref}
        className={cn(
          "cursor-default min-w-0 break-words",
          maxLines === 2 && "line-clamp-2",
          maxLines === 3 && "line-clamp-3",
          !maxLines && "truncate",
          inline ? "block leading-inherit" : "leading-snug",
          className,
        )}
      >
        {children}
      </span>
    </span>
  );

  if (!isTruncated) return inner;

  return (
    <Tooltip>
      <TooltipTrigger render={inner} />
      <TooltipContent side="top" className={cn("max-w-sm whitespace-pre-wrap", contentClassName)}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
}
